import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { loadingOrchestrator, LoadingState } from './core/orchestration/LoadingOrchestrator';

// Theme
import { ThemeProvider } from './contexts/ThemeContext';
import { SACRED_COLORS } from './utils/sacredTheme';

// Components
import SacredBackground from './components/SacredBackground';
import LoadingSpinner from './components/LoadingSpinner';
import CosmicLoadingScreen from './components/CosmicLoadingScreen';
import ErrorBoundary from './components/ErrorBoundary';
import EpilepsyWarning from './components/EpilepsyWarning';

// Master Dashboard - Unified Workspace
import MasterDashboard from './pages/MasterDashboard';
import AuthPage from './pages/AuthPage';

// Sacred Styles
import './styles/app.css';

/**
 * Main App Component
 * 
 */
const App: React.FC = () => {
  const [loadingState, setLoadingState] = React.useState<LoadingState>(loadingOrchestrator.getState());

  React.useEffect(() => {
    // ⚖️ Astraea: Subscribe to loading state changes
    const unsubscribe = loadingOrchestrator.subscribe(setLoadingState);

    // 🌓 Both: Start orchestrated loading sequence
    loadingOrchestrator.startLoading().catch(error => {
      console.error('🔥 Loading error:', error);
    });

    // Cleanup on unmount
    return () => {
      unsubscribe();
    };
  }, []); // Run once on mount

  // ⚖️ Astraea: Show enhanced cosmic loading UI during initialization
  if (!loadingState.isComplete) {
    return (
      <>
        <CosmicLoadingScreen 
          progress={loadingState.progress}
          message={loadingState.message}
          phase={loadingState.phase}
        />
        <EpilepsyWarning />
      </>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <div className="fr00t-app">
            <SacredBackground />
            
            <AnimatePresence mode="wait">
              <motion.div
                key="sacred-content"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.1 }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
              >
                {/* Unified Master Dashboard - All tools on one workspace */}
                <Routes>
                  <Route path="/" element={<MasterDashboard />} />
                  <Route path="/auth" element={<AuthPage />} />
                  {/* Legacy routes redirect to dashboard for now */}
                  <Route path="*" element={<MasterDashboard />} />
                </Routes>
              </motion.div>
            </AnimatePresence>

            {/* Sacred Toast Notifications */}
            <Toaster
              position="top-center"
              toastOptions={{
                duration: 2000, // Shorter duration to reduce spam
                style: {
                  background: SACRED_COLORS.darkCard,
                  color: SACRED_COLORS.light,
                  border: `1px solid ${SACRED_COLORS.purpleAlpha(0.3)}`,
                  borderRadius: '12px',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                },
                success: {
                  iconTheme: {
                    primary: SACRED_COLORS.emerald,
                    secondary: SACRED_COLORS.white,
                  },
                },
                error: {
                  iconTheme: {
                    primary: SACRED_COLORS.red,
                    secondary: SACRED_COLORS.white,
                  },
                },
              }}
            />
            <EpilepsyWarning />
          </div>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;

